/**
 * MissionCommandBar — the official reusable platform component.
 *
 * A Mission Command Bar (not a traditional breadcrumb): navigation + live
 * status + actionable work + contextual drill-down. See AO-002 and
 * canonical-breadcrumb.md §6. Studio breadcrumbs are thin wrappers that pass a
 * mission + workflows + counts config; NO per-studio rendering divergence.
 *
 * Each STAGE owns its properties (icon, color, subtitle, counter, drawer,
 * workflow, page) — read straight from the mission registry stage definition.
 *
 *   Row 1 → MISSION: <label> — responsive stage rail (single line ≥1280px,
 *           breathes on ≥1536px; cross-studio stages carry a destination badge)
 *   Row 2 → <active stage workflow> chips
 */
import { useState, useRef } from "react";
import { ChevronRight } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import MissionStageDrawer from "./MissionStageDrawer";

// Studios a stage can hand off to (renders a destination badge). "shared" and
// the bar's own home studio never get a badge.
const DESTINATION_STUDIOS = new Set(["brand", "creative", "sales", "projects", "finance", "operations"]);

function localized(obj, lang) {
  if (!obj) return "";
  return obj[lang] || obj.en || "";
}

function PulseDot({ color, testid }) {
  return (
    <span className="relative inline-flex w-2 h-2 ml-1 shrink-0" data-testid={testid} aria-hidden="true">
      <span className="absolute inset-0 rounded-full opacity-60 animate-ping" style={{ backgroundColor: color }} />
      <span className="relative inline-flex rounded-full w-2 h-2" style={{ backgroundColor: color }} />
    </span>
  );
}

export default function MissionCommandBar({
  mission,
  workflows,
  homeStudio,
  activeStageId,
  activePillarId,
  counts,
  stageRowsEndpoint,
  iconByName,
  ownerBadgeLabels,
  slug,
  drawerSlug,
  onNavigate,
}) {
  const { language, t } = useLanguage();
  const [openDrawer, setOpenDrawer] = useState(null);
  const badgeRefs = useRef({});
  const toggle = (key) => setOpenDrawer(openDrawer === key ? null : key);

  const missionAccent = mission.color;
  const FallbackIcon = iconByName.__fallback || Object.values(iconByName)[0];

  const stages = mission.stages.map((s) => ({
    ...s,
    label: localized(s.label, language),
    subtitle: localized(s.subtitle, language),
    Icon: iconByName[s.icon] || FallbackIcon,
    ownerBadge:
      s.owner_studio && s.owner_studio !== homeStudio && DESTINATION_STUDIOS.has(s.owner_studio)
        ? `\u2192 ${localized(ownerBadgeLabels[s.owner_studio], language)}`
        : null,
  }));

  const activeStage = stages.find((p) => p.id === activeStageId);
  const childAccent = activeStage?.color || missionAccent;
  const pillarWorkflow = activePillarId ? workflows[activePillarId] : null;
  const activeStageDrawable = !!activeStage?.drawable;

  return (
    <div className="w-full mb-3" data-testid={`${slug}-pillars-breadcrumb`}>
      {/* Mission label */}
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--app-text-muted)]">{t("Mission")}:</span>
        <span
          className="text-[10px] uppercase tracking-widest font-bold"
          style={{ color: missionAccent }}
          data-testid={`${slug}-pillars-breadcrumb-mission-label`}
        >
          {localized(mission.label, language)}
        </span>
      </div>

      {/* Row 1: responsive stage rail */}
      <div className="flex w-full items-stretch gap-1 2xl:gap-2" data-testid={`${slug}-pillars-breadcrumb-strip`}>
        {stages.map((it, i) => {
          const isCurrent = it.id === activeStageId;
          const accent = it.color;
          const pillStyle = isCurrent
            ? { borderColor: `${accent}88`, backgroundColor: `${accent}1f`, color: accent, boxShadow: `0 1px 0 ${accent}22, 0 0 0 1px ${accent}33` }
            : { borderColor: `${accent}33`, backgroundColor: `${accent}0d`, color: "var(--app-text)" };
          const numStyle = isCurrent
            ? { backgroundColor: `${accent}40`, color: accent }
            : { backgroundColor: `${accent}22`, color: `${accent}cc` };
          const badgeKey = `pillar:${it.id}`;
          const cnt = counts?.pillars?.[it.id];
          const hasCount = cnt != null && cnt > 0;
          const drawable = hasCount && !!it.drawable;
          return (
            <div key={it.id} className="flex items-center min-w-0 flex-1 relative" style={{ zIndex: stages.length - i }}>
              <button
                onClick={() => onNavigate(it.page)}
                className="group relative flex items-center gap-1 2xl:gap-1.5 rounded-lg px-1.5 py-1 2xl:px-2.5 2xl:py-1.5 border transition-all cursor-pointer w-full min-w-0 overflow-hidden hover:-translate-y-px"
                style={pillStyle}
                data-testid={`pillar-${it.id}`}
                data-current={isCurrent || undefined}
                title={`${it.label}${it.ownerBadge ? " " + it.ownerBadge : ""} — ${it.subtitle}`}
              >
                <span className="hidden 2xl:inline-flex w-4 h-4 2xl:w-5 2xl:h-5 items-center justify-center rounded-full text-[9px] 2xl:text-[10px] font-bold shrink-0" style={numStyle}>
                  {i + 1}
                </span>
                <span
                  className="inline-flex w-5 h-5 2xl:w-7 2xl:h-7 items-center justify-center rounded-full shrink-0 transition-transform group-hover:rotate-3"
                  style={{
                    backgroundColor: isCurrent ? `${accent}28` : `${accent}14`,
                    color: accent,
                    boxShadow: isCurrent ? `inset 0 0 0 1px ${accent}40` : `inset 0 0 0 1px ${accent}20`,
                  }}
                  data-testid={`pillar-icon-${it.id}`}
                >
                  <it.Icon className="w-3 h-3 2xl:w-3.5 2xl:h-3.5" strokeWidth={2.25} />
                </span>
                <span className="flex flex-col items-start min-w-0 flex-1 leading-[1.1] pr-0.5">
                  <span className="block w-full text-[11.5px] 2xl:text-[13px] font-semibold truncate">{it.label}</span>
                  {it.ownerBadge ? (
                    <span
                      className="block w-full text-[8px] 2xl:text-[9px] font-bold uppercase tracking-tight truncate"
                      style={{ color: `${accent}bb` }}
                      data-testid={`pillar-owner-badge-${it.id}`}
                      title={it.ownerBadge}
                    >
                      {it.ownerBadge}
                    </span>
                  ) : (
                    <span
                      className="hidden 2xl:block w-full text-[9px] 2xl:text-[10px] font-medium truncate opacity-75"
                      style={{ color: isCurrent ? `${accent}cc` : "var(--app-text-muted)" }}
                    >
                      {it.subtitle}
                    </span>
                  )}
                </span>
                {counts?.recent_24h?.pillars?.[it.id] && <PulseDot color={accent} testid={`pulse-pillar-${it.id}`} />}
                {hasCount && (drawable ? (
                <span
                  role="button"
                  tabIndex={0}
                  ref={(el) => { badgeRefs.current[badgeKey] = el; }}
                  onClick={(e) => { e.stopPropagation(); toggle(badgeKey); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); toggle(badgeKey); } }}
                  className="absolute right-1 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center min-w-[20px] h-[18px] rounded-full px-1.5 text-[10px] font-bold cursor-pointer hover:scale-110 transition-transform shadow-sm"
                  style={{ backgroundColor: isCurrent ? accent : `${accent}cc`, color: "var(--app-bg)" }}
                  title={t("Click to see oldest at this stage")}
                  data-testid={`pillar-count-${it.id}`}
                  data-count={cnt}
                  aria-expanded={openDrawer === badgeKey}
                >
                  {cnt}
                </span>
              ) : (
                <span
                  className="absolute right-1 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center min-w-[20px] h-[18px] rounded-full px-1.5 text-[10px] font-bold shadow-sm"
                  style={{ backgroundColor: isCurrent ? accent : `${accent}cc`, color: "var(--app-bg)" }}
                  data-testid={`pillar-count-${it.id}`}
                  data-count={cnt}
                >
                  {cnt}
                </span>
              ))}
              </button>
              {openDrawer === badgeKey && drawable && (
                <MissionStageDrawer
                  stage={it.id}
                  count={cnt || 0}
                  accent={accent}
                  anchorRef={{ current: badgeRefs.current[badgeKey] }}
                  endpoint={stageRowsEndpoint}
                  slug={drawerSlug}
                  onClose={() => setOpenDrawer(null)}
                />
              )}
              {i < stages.length - 1 && (
                <ChevronRight className="w-2.5 h-2.5 text-[var(--app-text-muted)] opacity-40 shrink-0 mx-0" />
              )}
            </div>
          );
        })}
      </div>

      {/* Row 2: active stage workflow */}
      {pillarWorkflow && (
        <div className="mt-2 pl-7 relative" data-testid={`${slug}-pillar-children-breadcrumb`}>
          <span
            aria-hidden="true"
            className="absolute top-0 left-2 w-3 h-[18px] border-l border-b rounded-bl-md pointer-events-none"
            style={{ borderColor: `${childAccent}55` }}
          />
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: childAccent }}>
              {localized(pillarWorkflow.label, language)}
            </span>
          </div>
          <div className="flex items-center gap-1 flex-wrap" data-testid={`${slug}-pillar-children-strip`}>
            {pillarWorkflow.items.map((child, ci) => {
              const isCurrentChild = window.location.pathname === child.page;
              const childPillStyle = isCurrentChild
                ? { borderColor: `${childAccent}88`, backgroundColor: `${childAccent}1f`, color: childAccent, boxShadow: `0 1px 0 ${childAccent}22, 0 0 0 1px ${childAccent}33` }
                : { borderColor: `${childAccent}26`, backgroundColor: `${childAccent}08`, color: "var(--app-text)" };
              const childNumStyle = isCurrentChild
                ? { backgroundColor: `${childAccent}40`, color: childAccent }
                : { backgroundColor: `${childAccent}1a`, color: `${childAccent}bb` };
              const childBadgeKey = `child:${child.id}`;
              const childCnt = counts?.children?.[child.id];
              const childHasCount = childCnt != null && childCnt > 0;
              const childDrawable = childHasCount && activeStageDrawable && !!activePillarId;
              return (
                <div key={child.id} className="flex items-center shrink-0">
                  <button
                    onClick={() => onNavigate(child.page)}
                    className="flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] 2xl:text-[12px] font-medium border transition-all cursor-pointer hover:scale-[1.02]"
                    style={childPillStyle}
                    data-testid={`pillar-child-${child.id}`}
                    data-current={isCurrentChild || undefined}
                  >
                    <span className="inline-flex w-4 h-4 items-center justify-center rounded-full text-[9px] font-bold shrink-0" style={childNumStyle}>
                      {ci + 1}
                    </span>
                    {localized(child.label, language)}
                    {counts?.recent_24h?.children?.[child.id] && <PulseDot color={childAccent} testid={`pulse-child-${child.id}`} />}
                    {childHasCount && (childDrawable ? (
                      <span
                        role="button"
                        tabIndex={0}
                        ref={(el) => { badgeRefs.current[childBadgeKey] = el; }}
                        onClick={(e) => { e.stopPropagation(); toggle(childBadgeKey); }}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); toggle(childBadgeKey); } }}
                        className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[16px] rounded-full px-1 text-[10px] font-bold cursor-pointer hover:scale-110 transition-transform"
                        style={{ backgroundColor: isCurrentChild ? childAccent : `${childAccent}cc`, color: "var(--app-bg)" }}
                        title={t("Click to see oldest at this stage")}
                        data-testid={`pillar-child-count-${child.id}`}
                        data-count={childCnt}
                        aria-expanded={openDrawer === childBadgeKey}
                      >
                        {childCnt}
                      </span>
                    ) : (
                      <span
                        className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[16px] rounded-full px-1 text-[10px] font-bold"
                        style={{ backgroundColor: isCurrentChild ? childAccent : `${childAccent}cc`, color: "var(--app-bg)" }}
                        data-testid={`pillar-child-count-${child.id}`}
                        data-count={childCnt}
                      >
                        {childCnt}
                      </span>
                    ))}
                  </button>
                  {openDrawer === childBadgeKey && childDrawable && (
                    <MissionStageDrawer
                      stage={activePillarId}
                      count={childCnt || 0}
                      accent={childAccent}
                      anchorRef={{ current: badgeRefs.current[childBadgeKey] }}
                      endpoint={stageRowsEndpoint}
                      slug={drawerSlug}
                      onClose={() => setOpenDrawer(null)}
                    />
                  )}
                  {ci < pillarWorkflow.items.length - 1 && (
                    <ChevronRight className="w-3 h-3 text-[var(--app-text-muted)] opacity-40 shrink-0 mx-0.5" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
